/**
 * Mixpanel event names. Use with trackEvent(EventName.X, { ... }).
 *
 * Naming: "Landing Web [Page] [Element] [Action]"
 * - Page: screen/section (Home, Find Help, Header, Footer, Blog, GuideEnroll, etc.)
 * - Element: button, link, or control name (Get Started, Find Help, Terms, etc.)
 * - Action: Clicked, Focused, Started, Stopped, etc.
 *
 * Typed event properties and helpers: see eventNames.d.ts (EventProperties, PropsFor, TrackEventArgs).
 */
export const EventName = Object.freeze({
  // Homepage
  HomeNewsletterSubscribeClicked:
    "Landing Web Home Newsletter Subscribe Clicked",
  // Homepage (2026 redesign). CTAs sharing a destination screen use one generic
  // event differentiated by a `section` property; section-unique actions get their own.
  HomeGetTenderIdClicked: "Landing Web Home Get TenderID Clicked", // → tenderID onboarding; props: { section }
  HomeFindHelpClicked: "Landing Web Home Find Help Clicked", // → resource matching; props: { section, tab }
  HomeDownloadAppClicked: "Landing Web Home Download App Clicked", // → download modal; props: { section, tab }
  HomeTrustedNetworkClicked: "Landing Web Home Trusted Network Clicked", // → /trusted-network; props: { section }
  HomeTenderIdLearnMoreClicked: "Landing Web Home TenderID Learn More Clicked", // → /tenderid; props: { section, tab }
  HomeTenderIdPreviewHovered: "Landing Web Home TenderID Preview Hovered", // hovers the scan preview image; props: { section }
  HomeTenderIdPreviewOpened: "Landing Web Home TenderID Preview Opened", // → opens scan iframe preview; props: { section }
  HomeTenderIdPreviewClosed: "Landing Web Home TenderID Preview Closed", // closes the scan iframe preview (X); props: { section }
  HomeGuideCheckEligibilityClicked:
    "Landing Web Home Guide Check Eligibility Clicked", // props: { section, tab? }
  HomeGuideLearnMoreClicked: "Landing Web Home Guide Learn More Clicked", // props: { section }
  HomeUseCaseTabClicked: "Landing Web Home Use Case Tab Clicked", // props: { section, tab }
  HomeCategoryClicked: "Landing Web Home Category Clicked", // props: { section, category }
  HomeVettingModalOpened: "Landing Web Home Vetting Modal Opened", // props: { section }
  // Home Tab – Find Help component
  HomeTabInputFocused: "Landing Web Home Tab Input Focused",
  HomeTabSubmitClicked: "Landing Web Home Tab Submit Clicked",
  // Home Tab – Vimeo player
  HomeTabVimeoPlayClicked: "Landing Web Home Tab Vimeo Play Clicked",
  HomeTabVimeoPauseClicked: "Landing Web Home Tab Vimeo Pause Clicked",
  HomeTabVimeoPlay: "Landing Web Home Tab Vimeo Play",
  HomeTabVimeoPause: "Landing Web Home Tab Vimeo Pause",
  HomeTabVimeoEnded: "Landing Web Home Tab Vimeo Ended",
  // Home Tab – voice input (dictate)
  HomeTabDictateStarted: "Landing Web Home Tab Dictate Started",
  HomeTabDictateStopped: "Landing Web Home Tab Dictate Stopped",
  HomeTabDictateClickedUnavailable:
    "Landing Web Home Tab Dictate Clicked Unavailable",
  HomeTabDictatePermissionDenied:
    "Landing Web Home Tab Dictate Permission Denied",
  HomeTabDictateTranscribeSuccess:
    "Landing Web Home Tab Dictate Transcribe Success",
  HomeTabDictateTranscribeError:
    "Landing Web Home Tab Dictate Transcribe Error",
  HomeTabDictateCancelled: "Landing Web Home Tab Dictate Cancelled",
  // Home Tab – Tabs
  HomeTabFindHelpTabClicked: "Landing Web Home Tab Find Help Tab Clicked",
  HomeTabManagePrepareTabClicked:
    "Landing Web Home Tab Manage Prepare Tab Clicked",
  HomeTabFreeEmergencyCardsTabClicked:
    "Landing Web Home Tab Free Emergency Cards Tab Clicked",
  // Home Tab – Category
  HomeTabCategoryClicked: "Landing Web Home Tab Category Clicked",
  // Home Tab – Manage Everything
  HomeTabManageEverythingMyselfClicked:
    "Landing Web Home Tab Manage Everything Myself Clicked",
  HomeTabManageEverythingMyselfFamilyClicked:
    "Landing Web Home Tab Manage Everything Myself Family Clicked",
  HomeTabManageEverythingLovedOneClicked:
    "Landing Web Home Tab Manage Everything Loved One Clicked",
  // Moments Carousel
  MomentsCarouselVideoCardClicked:
    "Landing Web Moments Carousel Video Card Clicked",
  MomentsCarouselVideoModalOpened:
    "Landing Web Moments Carousel Video Modal Opened",
  MomentsCarouselVideoPlay: "Landing Web Moments Carousel Video Play",
  MomentsCarouselVideoPause: "Landing Web Moments Carousel Video Pause",
  MomentsCarouselVideoEnded: "Landing Web Moments Carousel Video Ended",
  MomentsCarouselTextCardClicked:
    "Landing Web Moments Carousel Text Card Clicked",
  // Vetted Professionals
  VettedProfessionalsSearchInputFocused:
    "Landing Web Vetted Professionals Search Input Focused",
  VettedProfessionalsSearchSubmitClicked:
    "Landing Web Vetted Professionals Search Submit Clicked",
  VettedProfessionalsCategoryClicked:
    "Landing Web Vetted Professionals Category Clicked",
  VettedProfessionalsVideoCardClicked:
    "Landing Web Vetted Professionals Video Card Clicked",
  VettedProfessionalsVideoModalOpened:
    "Landing Web Vetted Professionals Video Modal Opened",
  VettedProfessionalsVideoPlay: "Landing Web Vetted Professionals Video Play",
  VettedProfessionalsVideoPause: "Landing Web Vetted Professionals Video Pause",
  VettedProfessionalsVideoEnded: "Landing Web Vetted Professionals Video Ended",
  FindHelpCategoryBannerSearchInputFocused:
    "Landing Web Find Help Category Banner Search Input Focused",
  FindHelpCategoryTrustedProvidersZipSearchInputFocused:
    "Landing Web Find Help Category Trusted Providers Zip Search Input Focused",
  FindHelpCategoryReadyToFindCtaSearchInputFocused:
    "Landing Web Find Help Category Ready To Find CTA Search Input Focused",
  FindHelpCategoryBannerSearchSubmitClicked:
    "Landing Web Find Help Category Banner Search Submit Clicked",
  FindHelpCategoryTrustedProvidersZipSearchSubmitClicked:
    "Landing Web Find Help Category Trusted Providers Zip Search Submit Clicked",
  FindHelpCategoryReadyToFindCtaSearchSubmitClicked:
    "Landing Web Find Help Category Ready To Find CTA Search Submit Clicked",
  FindHelpCategorySidebarPrimaryCtaClicked:
    "Landing Web Find Help Category Sidebar Primary CTA Clicked",
  FindHelpCategoryTrustedPartnerClicked:
    "Landing Web Find Help Category Trusted Partner Clicked",
  FindHelpCategoryTrustedNetworkDirectoryClicked:
    "Landing Web Find Help Category Trusted Network Directory Clicked",
  FindHelpCategoryFaqToggled: "Landing Web Find Help Category FAQ Toggled",
  FindHelpCategoryRelatedCategoryClicked:
    "Landing Web Find Help Category Related Category Clicked",
  FindHelpCategoryAdditionalCategoriesToggled:
    "Landing Web Find Help Category Additional Categories Toggled",
  FindHelpCategoryAdditionalCategoryLinkClicked:
    "Landing Web Find Help Category Additional Category Link Clicked",
  FindHelpCategoryCareNavigatorEmailClicked:
    "Landing Web Find Help Category Care Navigator Email Clicked",
  FindHelpCategoryContactGuideSubmitted:
    "Landing Web Find Help Category Contact Guide Submitted",
  FindHelpCategoryContactGuideSucceeded:
    "Landing Web Find Help Category Contact Guide Succeeded",
  FindHelpCategoryContactGuideFailed:
    "Landing Web Find Help Category Contact Guide Failed",
  // Features Section
  FeaturesSectionFindHelpClicked:
    "Landing Web Features Section Find Help Clicked",
  FeaturesSectionLearnMoreClicked:
    "Landing Web Features Section Learn More Clicked",
  // TenderID
  TenderIDGetFreeClicked: "Landing Web TenderID Get Free Clicked",
  // App Download
  AppDownloadIOSClicked: "Landing Web App Download iOS Clicked",
  AppDownloadAndroidClicked: "Landing Web App Download Android Clicked",
  // Home App Callout (scroll-triggered download promo: desktop card + mobile bar)
  HomeAppCalloutIOSClicked: "Landing Web Home App Callout iOS Clicked", // props: { device, variant }
  HomeAppCalloutAndroidClicked: "Landing Web Home App Callout Android Clicked", // props: { device, variant }
  HomeAppCalloutDismissed: "Landing Web Home App Callout Dismissed", // props: { device, variant }
  // Trusted Partners
  TrustedPartnersLogoClicked: "Landing Web Trusted Partners Logo Clicked",
  // Caregivers Section
  CaregiversSectionFindHelpClicked:
    "Landing Web Caregivers Section Find Help Clicked",
  CaregiversSectionLearnMoreClicked:
    "Landing Web Caregivers Section Learn More Clicked",
  // Header (global nav)
  HeaderTenderIdBannerClicked: "Landing Web Header TenderID Banner Clicked",
  HeaderTenderIdClicked: "Landing Web Header TenderID Clicked",
  HeaderGuideClicked: "Landing Web Header Guide Clicked",
  HeaderTrustedNetworkClicked: "Landing Web Header Trusted Network Clicked",
  HeaderJoinNetworkClicked: "Landing Web Header Join Network Clicked",
  HeaderMemberHubClicked: "Landing Web Header Member Hub Clicked",
  HeaderCodeOfConductClicked: "Landing Web Header Code Of Conduct Clicked",
  HeaderForCaregiversClicked: "Landing Web Header For Caregivers Clicked",
  HeaderForEmployersClicked: "Landing Web Header For Employers Clicked",
  HeaderForHealthPlansClicked: "Landing Web Header For Health Plans Clicked",
  HeaderMissionClicked: "Landing Web Header Mission Clicked",
  HeaderOurValuesClicked: "Landing Web Header Our Values Clicked",
  HeaderOurStoryClicked: "Landing Web Header Our Story Clicked",
  HeaderTeamClicked: "Landing Web Header Team Clicked",
  HeaderBlogClicked: "Landing Web Header Blog Clicked",
  HeaderSecurityClicked: "Landing Web Header Security Clicked",
  HeaderContactClicked: "Landing Web Header Contact Clicked",
  HeaderGetStartedClicked: "Landing Web Header Get Started Clicked",
  HeaderLoginClicked: "Landing Web Header Login Clicked",
  HeaderLogoClicked: "Landing Web Header Logo Clicked",
  HeaderBecomeTbdMemberClicked: "Landing Web Header Become TBD Member Clicked",
  HeaderMenuOpened: "Landing Web Header Menu Opened",
  HeaderMenuClosed: "Landing Web Header Menu Closed",
  HeaderNavDropdownOpened: "Landing Web Header Nav Dropdown Opened",
  HeaderFindHelpCategoryClicked:
    "Landing Web Header Find Help Category Clicked",
  HeaderFindTrustedHelpClicked: "Landing Web Header Find Trusted Help Clicked",
  HeaderAboutUsClicked: "Landing Web Header About Us Clicked",
  // Footer (global)
  FooterSupportClicked: "Landing Web Footer Support Clicked",
  FooterCareersClicked: "Landing Web Footer Careers Clicked",
  FooterContactClicked: "Landing Web Footer Contact Clicked",
  FooterPressClicked: "Landing Web Footer Press Clicked",
  FooterLinkedInClicked: "Landing Web Footer LinkedIn Clicked",
  FooterFacebookClicked: "Landing Web Footer Facebook Clicked",
  FooterInstagramClicked: "Landing Web Footer Instagram Clicked",
  FooterThreadsClicked: "Landing Web Footer Threads Clicked",
  FooterYouTubeClicked: "Landing Web Footer YouTube Clicked",
  FooterAppStoreClicked: "Landing Web Footer App Store Clicked",
  FooterPlayStoreClicked: "Landing Web Footer Play Store Clicked",
  FooterTermsClicked: "Landing Web Footer Terms Clicked",
  FooterPrivacyClicked: "Landing Web Footer Privacy Clicked",
  FooterFaqsClicked: "Landing Web Footer FAQs Clicked",
  FooterForAiClicked: "Landing Web Footer For AI Clicked",
  FooterRobotsClicked: "Landing Web Footer Robots Clicked",
  FooterSitemapClicked: "Landing Web Footer Sitemap Clicked",
  HeaderFaqsClicked: "Landing Web Header FAQs Clicked",
  // Blog
  BlogFilterClicked: "Landing Web Blog Filter Clicked",
  BlogSortClicked: "Landing Web Blog Sort Clicked",
  BlogPostClicked: "Landing Web Blog Post Clicked",
  // Other pages
  GuideEnrollCheckEligibilityClicked:
    "Landing Web Guide Enroll Check Eligibility Clicked",
  GuideSupportContactClicked: "Landing Web Guide Support Contact Clicked",
  GuideFAQToggled: "Landing Web Guide FAQ Toggled",
  MagnetGetStartedClicked: "Landing Web Magnet Get Started Clicked",
  ForCaregiversGetStartedClicked:
    "Landing Web For Caregivers Get Started Clicked",
  ForEmployersCtaClicked: "Landing Web For Employers CTA Clicked",
  ForHealthPlansCtaClicked: "Landing Web For Health Plans CTA Clicked",
  AboutUsCtaClicked: "Landing Web About Us CTA Clicked",
  MedicareSuccessAppStoreClicked:
    "Landing Web Medicare Success App Store Clicked",
  MedicareErrorAppStoreClicked: "Landing Web Medicare Error App Store Clicked",
  BeanstalkAppStoreClicked: "Landing Web Beanstalk App Store Clicked",
  CodeOfConductExternalLinkClicked:
    "Landing Web Code Of Conduct External Link Clicked",
  CodeOfConductContactClicked: "Landing Web Code Of Conduct Contact Clicked",
  SecurityContactClicked: "Landing Web Security Contact Clicked",
  RequestDemoClicked: "Landing Web Request Demo Clicked",
  // Generic button (DSButton)
  ButtonPressed: "Landing Web Button Pressed",
  // Trusted Network – service/member public profile
  TrustedNetworkMemberPublicProfileViewed:
    "Landing Web Trusted Network Member Public Profile Viewed",
  TrustedNetworkMemberPublicProfileContactClicked:
    "Landing Web Trusted Network Member Public Profile Contact Clicked",
  TrustedNetworkMemberPublicProfileCustomContactClicked:
    "Landing Web Trusted Network Member Public Profile Custom Contact Clicked",
  TrustedNetworkMemberPublicProfileContactProviderSuccess:
    "Landing Web Trusted Network Member Public Profile Contact Provider Success",
  // Service details
  WebServiceVideoPlayed: "Landing Web Service Video Played",
  WebNewAssessmentStarted: "Landing Web New Assessment Started",
  WebServiceFAQToggled: "Landing Web Service FAQ Toggled",
  WebServiceHeroVideoPlayed: "Landing Web Service Hero Video Played",
  WebServiceHeroVideoPaused: "Landing Web Service Hero Video Paused",
  WebServiceHeroVideoMuted: "Landing Web Service Hero Video Muted",
  WebServiceHeroVideoUnmuted: "Landing Web Service Hero Video Unmuted",
  WebServiceHeroVideoReplayed: "Landing Web Service Hero Video Replayed",
  WebServiceHeroVideoCompleted: "Landing Web Service Hero Video Completed",
  WebServiceProfileVideoModalClosed:
    "Landing Web Service Profile Video Modal Closed",
  WebServiceProfileVideoModalMuted:
    "Landing Web Service Profile Video Modal Muted",
  WebServiceProfileVideoModalUnmuted:
    "Landing Web Service Profile Video Modal Unmuted",
  WebServiceProfileVideoModalPlayToggled:
    "Landing Web Service Profile Video Modal Play Toggled",
  WebServiceRatingBlockClicked: "Landing Web Service Rating Block Clicked",
  WebServiceCategoryDescriptionOpened:
    "Landing Web Service Category Description Opened",
  WebServiceAcronymTooltipViewed: "Landing Web Service Acronym Tooltip Viewed",
  WebServiceSectionNavClicked: "Landing Web Service Section Nav Clicked",
  WebServiceGetIntroducedClicked: "Landing Web Service Get Introduced Clicked",
  WebServiceTrustedNetworkModalOpened:
    "Landing Web Service Trusted Network Modal Opened",
  WebServiceTrustedNetworkModalClosed:
    "Landing Web Service Trusted Network Modal Closed",
  WebServiceTrustedNetworkLicenseInfoClicked:
    "Landing Web Service Trusted Network License Info Clicked",
  WebServiceShowMoreToggled: "Landing Web Service Show More Toggled",
  WebServiceLocationMatchClicked: "Landing Web Service Location Match Clicked",
  // Trusted Network page
  TrustedNetworkHeroFindHelpClicked:
    "Landing Web Trusted Network Hero Find Help Clicked",
  TrustedNetworkHeroJoinNetworkClicked:
    "Landing Web Trusted Network Hero Join Network Clicked",
  TrustedNetworkCtaLearnMoreClicked:
    "Landing Web Trusted Network CTA Learn More Clicked",
  TrustedNetworkCareSmarterDownloadClicked:
    "Landing Web Trusted Network Care Smarter Download Clicked",
  TrustedNetworkVerifiedMemberLearnMoreClicked:
    "Landing Web Trusted Network Verified Member Learn More Clicked",
  TrustedNetworkCategoriesConnectClicked:
    "Landing Web Trusted Network Categories Connect Clicked",
  TrustedNetworkCategoryClicked: "Landing Web Trusted Network Category Clicked",
  TrustedNetworkCategoryModalClosed:
    "Landing Web Trusted Network Category Modal Closed",
  TrustTransparencyBillOfRightsClicked:
    "Landing Web Trust Transparency Bill Of Rights Clicked",
  TrustedNetworkVerifiedMemberCodeOfConductClicked:
    "Landing Web Trusted Network Verified Member Code Of Conduct Clicked",
  FreeModalTrustedNetworkClicked:
    "Landing Web Free Modal Trusted Network Clicked",
  DownloadModalAppStoreClicked: "Landing Web Download Modal App Store Clicked",
  DownloadModalPlayStoreClicked:
    "Landing Web Download Modal Play Store Clicked",
  DownloadModalClosed: "Landing Web Download Modal Closed",
  FreeModalClosed: "Landing Web Free Modal Closed",
  NewsletterModalClosed: "Landing Web Newsletter Modal Closed",
  NewsletterCtaDismissed: "Landing Web Newsletter CTA Dismissed",
  AboutUsInvestorClicked: "Landing Web About Us Investor Clicked",
  PressSectionLogoClicked: "Landing Web Press Section Logo Clicked",
  WideMediaVideoPlayClicked: "Landing Web Wide Media Video Play Clicked",
  FaqLinkClicked: "Landing Web FAQ Link Clicked",
  ColumnStatsSourceClicked: "Landing Web Column Stats Source Clicked",
  // About Us – Team
  AboutUsTeamMemberClicked: "Landing Web About Us Team Member Clicked",
  AboutUsTeamMemberModalClosed: "Landing Web About Us Team Member Modal Closed",
})
